import React, { useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './InfoTooltip.css';

interface InfoTooltipProps {
  /**
   * ReactNode, not string: multi-paragraph bodies pass a fragment of <p>s.
   * MUI's Tooltip `title` already accepts ReactNode, so this only widens the
   * local narrowing. Every existing caller passes a string and is unaffected.
   */
  text: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  /**
   * Wraps the icon in an unstyled, focusable button on a controlled Tooltip,
   * so the content is reachable on hover, on keyboard focus, AND on tap.
   * The click toggle is what makes a quick tap work: MUI's own touch handling
   * clears its enter timer when the finger lifts, so touch alone only opens
   * on long-press. Default false: existing consumers keep the bare
   * hover-only icon, rendered exactly as before.
   */
  focusable?: boolean;
  /** Accessible name for the focusable trigger (focusable mode only) */
  ariaLabel?: string;
  /**
   * Content rendered INSIDE the trigger, ahead of the icon. Passing a label
   * here makes the whole label the hit target instead of the ~14px glyph — a
   * tiny target inside a much longer line is a miss most of the time. The icon
   * stays visible either way: it is the affordance signal.
   */
  triggerLabel?: React.ReactNode;
  /**
   * `panel` is the dark surface treatment: near-black plum, pink hairline
   * border, and typographic sections. Opt-in, because the default grey is
   * still correct for the dense admin tables this component started on.
   */
  variant?: 'default' | 'panel';
}

/**
 * InfoTooltip Component
 * Displays an info icon with a tooltip on hover.
 * Shared help-tooltip used for metric and table-column context.
 * With `focusable`, the trigger is a real button: hover + focus + tap.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  placement = 'top',
  focusable = false,
  ariaLabel = 'More info',
  triggerLabel,
  variant = 'default',
}) => {
  const [open, setOpen] = useState(false);
  // A tap/click PINS the tooltip open: the tap gesture emits a synthetic
  // mouseleave right after click, which would otherwise close it instantly.
  // Pinned ignores hover-out; blur or a second click unpins.
  const pinnedRef = useRef(false);

  // The panel skin is applied to MUI's own tooltip and arrow slots, so the
  // caret is tinted to the panel background rather than staying default grey.
  const slotProps =
    variant === 'panel'
      ? {
          tooltip: { className: 'info-tooltip-panel', 'data-testid': 'info-tooltip-panel' },
          arrow: { className: 'info-tooltip-panel-arrow', 'data-testid': 'info-tooltip-panel-arrow' },
        }
      : undefined;

  if (focusable) {
    return (
      <Tooltip
        title={text}
        placement={placement}
        arrow
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          if (!pinnedRef.current) setOpen(false);
        }}
        enterDelay={200}
        leaveDelay={0}
        slotProps={slotProps}
      >
        <button
          type="button"
          className={`info-tooltip-trigger${
            triggerLabel ? ' info-tooltip-trigger--labelled' : ''
          }`}
          /* With a visible label the button names itself; an aria-label would
             override that text for screen readers and hide the number. */
          aria-label={triggerLabel ? undefined : ariaLabel}
          onClick={() =>
            setOpen((prev) => {
              pinnedRef.current = !prev;
              return !prev;
            })
          }
          onBlur={() => {
            pinnedRef.current = false;
            setOpen(false);
          }}
          /* Escape dismisses without moving focus — the expected way out of a
             tooltip opened by keyboard. */
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              pinnedRef.current = false;
              setOpen(false);
            }
          }}
        >
          {triggerLabel}
          <InfoOutlinedIcon className="info-tooltip-icon" data-testid="info-tooltip-icon" />
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={text}
      placement={placement}
      arrow
      enterDelay={200}
      leaveDelay={0}
      slotProps={slotProps}
    >
      <InfoOutlinedIcon className="info-tooltip-icon" />
    </Tooltip>
  );
};

export default InfoTooltip;
